import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from 'ui/atoms/Icon/Icon'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import PublishRocketIcon from '../../PublishRocketIcon/PublishRocketIcon'
import { DialogContentData, SubtypePublishInfo } from '../PublishOptimizationResultButton.d'

type DialogPlanVisualizationProps = {
  dialogContent: DialogContentData
}

function SubtypeRow({ info }: { info: SubtypePublishInfo }): ReactElement | null {
  const { t } = useTranslation()

  if (!info.latestWipTimestamp) {
    return null
  }

  const subtypeSuffix = info.subtypeDisplayName ? ` ${info.subtypeDisplayName}` : ''

  // Initial publish - no previous published version
  if (info.isInitialPublish) {
    return (
      <div className="flex items-center gap-4 my-2">
        <PublishRocketIcon status="active" />
        <p>
          {t('Production plan')}
          {subtypeSuffix} {info.latestWipTimestamp}
        </p>
      </div>
    )
  }

  // Has both WIP and published - show transition
  return (
    <div className="flex flex-row items-center justify-center gap-16 my-2">
      <div className="flex flex-row gap-x-2 items-center justify-end">
        <Icon icon="fas fa-check-circle" color="grey" size="extra-large" />
        <p>
          {t('Production plan')}
          {subtypeSuffix} <br />
          {info.latestPublishedTimestamp}
        </p>
      </div>

      <Icon icon="fal fa-long-arrow-right" color="black" size="large" />

      <div className="flex flex-col">
        <div className="flex flex-row gap-x-2 items-center">
          <PublishRocketIcon status={info.alreadyPublished ? 'inactive' : 'active'} size="large" />

          <p>
            {t('Production plan')}
            {subtypeSuffix} <br />
            {info.latestWipTimestamp}
          </p>
        </div>
        {info.alreadyPublished && <div className="text-amber-600 mt-1 text-sm ml-9">({t('Already published')})</div>}
      </div>
    </div>
  )
}

export default function DialogPlanVisualization({ dialogContent }: DialogPlanVisualizationProps): ReactElement | null {
  const { t } = useTranslation()
  const { state, subtypes, isMultipleSubtypes, allAlreadyPublished } = dialogContent

  if (state === 'loading') {
    return null
  }

  if (state === 'empty') {
    return <InfoBanner style="info" text={t('There is no production plan to publish')} />
  }

  // Multiple subtypes - show each as a row
  if (isMultipleSubtypes) {
    return (
      <>
        <div className="flex flex-col my-4 gap-y-3">
          {subtypes.map((info) => (
            <SubtypeRow key={info.subtype ?? 'default'} info={info} />
          ))}
        </div>

        {allAlreadyPublished && <InfoBanner style="info" text={t('All production plans are already deployed')} />}
      </>
    )
  }

  // Single subtype or no subtypes
  const singleInfo = subtypes[0]
  if (!singleInfo) {
    return null
  }

  if (state === 'ok_to_publish') {
    return (
      <>
        <div className="flex flex-col my-4">
          <SubtypeRow info={singleInfo} />
        </div>

        {singleInfo.alreadyPublished && <InfoBanner style="info" text={t('Production plan already deployed')} />}
      </>
    )
  }

  // state === 'initial'
  return (
    <div className="flex flex-col my-4">
      <SubtypeRow info={singleInfo} />
    </div>
  )
}
